import type {
  Jackpot,
  JackpotWin,
  JackpotWins,
  Lootbox,
  PlayerBonus,
  PlayerFreespin,
  PlayerTransaction,
  ReferralProgramRealTime,
} from 'api/common';
import { JackpotStage } from 'api/common';
import produce from 'immer';
import { filterJackpotWinsListFromTest } from './helpers';

export type State = {
  bonuses: PlayerBonus[];
  bonusesPending: boolean;
  freespins: PlayerFreespin[];
  freespinsPending: boolean;
  lootboxes: Lootbox[];
  jackpots: Jackpot[];
  activeJackpot?: Jackpot;
  jackpotWins: JackpotWin[];
  jackpotWinsList: JackpotWins[];
  playerPayments: PlayerTransaction[];
  referralProgram: ReferralProgramRealTime | null;
};

export enum ACTION_TYPE {
  SET_LOOTBOXES = 0,
  SET_JACKPOTS = 1,
  SET_ACTIVE_JACKPOT = 2,
  ADD_LOOTBOX = 3,
  ADD_JACKPOT_WIN = 4,
  UPDATE_JACKPOT = 5,
  SET_JACKPOT_WINS_LIST = 6,
  ADD_PAYMENT = 7,
  REFERRAL_PROGRAM = 8,
  SET_BONUSES = 9,
  SET_FREESPINS = 10,
  UPDATE_BONUSES = 11,
  UPDATE_FREESPINS = 12,
}

export type Action =
  | {
      type: ACTION_TYPE.SET_BONUSES;
      payload: { bonuses: PlayerBonus[] };
    }
  | {
      type: ACTION_TYPE.SET_FREESPINS;
      payload: { freespins: PlayerFreespin[] };
    }
  | {
      type: ACTION_TYPE.UPDATE_BONUSES;
      payload: { bonus: PlayerBonus };
    }
  | {
      type: ACTION_TYPE.UPDATE_FREESPINS;
      payload: { freespin: PlayerFreespin };
    }
  | {
      type: ACTION_TYPE.SET_LOOTBOXES;
      payload: { lootboxes: Lootbox[] };
    }
  | {
      type: ACTION_TYPE.SET_JACKPOTS;
      payload: { jackpots: Jackpot[] };
    }
  | {
      type: ACTION_TYPE.ADD_LOOTBOX;
      payload: { lootbox: Lootbox };
    }
  | {
      type: ACTION_TYPE.UPDATE_JACKPOT;
      payload: { jackpot: Jackpot };
    }
  | {
      type: ACTION_TYPE.SET_ACTIVE_JACKPOT;
    }
  | {
      type: ACTION_TYPE.ADD_JACKPOT_WIN;
      payload: { jackpotWin: JackpotWin };
    }
  | {
      type: ACTION_TYPE.SET_JACKPOT_WINS_LIST;
      payload: { jackpotWinsList: JackpotWins[] };
    }
  | {
      type: ACTION_TYPE.ADD_PAYMENT;
      payload: { payment: PlayerTransaction };
    }
  | {
      type: ACTION_TYPE.REFERRAL_PROGRAM;
      payload: { referralProgram: ReferralProgramRealTime };
    };

type Reducer = (state: State, action: Action) => State;

const updateBonus = <T extends { id: number }>(
  bonuses: T[],
  incomingBonus: T
) => {
  const isIncomingBonusOld = bonuses.some(({ id }) => incomingBonus.id === id);

  return isIncomingBonusOld
    ? bonuses.map((bonus) =>
        bonus.id === incomingBonus.id ? incomingBonus : bonus
      )
    : [...bonuses, incomingBonus];
};

export const reducer: Reducer = (state, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ACTION_TYPE.SET_LOOTBOXES:
        draft.lootboxes = action.payload.lootboxes;
        break;
      case ACTION_TYPE.ADD_LOOTBOX:
        draft.lootboxes.push({ ...action.payload.lootbox });
        break;
      case ACTION_TYPE.SET_JACKPOTS:
        draft.jackpots = action.payload.jackpots;
        break;
      case ACTION_TYPE.UPDATE_JACKPOT:
        draft.jackpots = draft.jackpots.map((jackpot) =>
          jackpot.id === action.payload.jackpot.id
            ? action.payload.jackpot
            : jackpot
        );
        break;
      case ACTION_TYPE.SET_ACTIVE_JACKPOT:
        draft.activeJackpot = draft.jackpots.find(
          (jackpot) => jackpot.state === JackpotStage.Active
        );
        break;
      case ACTION_TYPE.SET_JACKPOT_WINS_LIST:
        draft.jackpotWinsList = filterJackpotWinsListFromTest(
          action.payload.jackpotWinsList
        );
        break;
      case ACTION_TYPE.ADD_JACKPOT_WIN:
        draft.jackpotWins.push({ ...action.payload.jackpotWin });
        break;
      case ACTION_TYPE.ADD_PAYMENT:
        draft.playerPayments.push(action.payload.payment);
        break;
      case ACTION_TYPE.REFERRAL_PROGRAM:
        draft.referralProgram = action.payload.referralProgram;
        break;
      case ACTION_TYPE.SET_BONUSES:
        draft.bonuses = action.payload.bonuses;
        draft.bonusesPending = false;
        break;
      case ACTION_TYPE.SET_FREESPINS:
        draft.freespins = action.payload.freespins;
        draft.freespinsPending = false;
        break;
      case ACTION_TYPE.UPDATE_BONUSES:
        draft.bonuses = updateBonus(draft.bonuses, action.payload.bonus);
        break;
      case ACTION_TYPE.UPDATE_FREESPINS:
        draft.freespins = updateBonus(draft.freespins, action.payload.freespin);
        break;
    }
  });
